/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
*/

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, CssBaseline, Typography, Box } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useAuthContext } from '@asgardeo/auth-react';
import { ApplicationState, StorageOperations } from '../../data/interfaces';
import { connect } from 'react-redux';
import { obtainApimTokenAndChangeOrganization } from '../../data/operations';
import { SELECTED_ORG_HANDLE_NAME, FORBIDDEN_SELECTED_ORG_HANDLE_NAME } from '../../data/constants';
import { localStoreOperation } from '../../data/utils';
import OutlineButton from '../../elements/OutlineButton';

const useStyles = makeStyles((theme: Theme) => ({
  signInContainer: {
    ...theme.custom.clientErrorPage.signInContainer.styles
  },
  signInText: {
    ...theme.custom.clientErrorPage.signInText.styles
  },
  loaderContainer: {
    ...theme.custom.clientErrorPage.loaderContainer.styles
  },
  alignCenter: {
    ...theme.custom.clientErrorPage.alignCenter.styles
  },
  noCapitalise: {
    textTransform: 'none',
  }
}));

function Forbidden() {

  const classes = useStyles();
  const intl = useIntl();
  const { signOut } = useAuthContext();

  const selectedOrgHandleName = localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME) || '';
  localStoreOperation(StorageOperations.ADD, FORBIDDEN_SELECTED_ORG_HANDLE_NAME, selectedOrgHandleName);
  const forbiddenOrgName = localStoreOperation(StorageOperations.GET, FORBIDDEN_SELECTED_ORG_HANDLE_NAME)

  const redirectToHomePage = () => {
    signOut();
  }

  return (
    <div>
      <CssBaseline />
      <Container maxWidth="lg" className={classes.signInContainer}>
        <div className={classes.loaderContainer}>
          <img
            src="/themes/default/images/unauthorized.svg"
            alt="No Organization Access"
          />
        </div>
        <Typography className={classes.signInText}>
          <FormattedMessage
            id="pages.ForbiddenAccess.title"
            defaultMessage="Access Forbidden"
          />
        </Typography>
        <Typography>
          <FormattedMessage
            id="pages.ForbiddenAccess.body"
            defaultMessage="You do not have required permission to access the organization {orgName} You may request access by contacting the organization admin"
            values={{
              orgName: <b>{forbiddenOrgName}<br/></b>,
            }}
          />
        </Typography>
        <Box mt={2}>
          <OutlineButton
            data-testid="back-to-home"
            className={classes.noCapitalise}
            onClick={() => redirectToHomePage()}
            buttonLabel={intl.formatMessage({
              id: "pages.NoOrgAccess.goBack.button",
              defaultMessage: "Sign Out and Go Back",
            })}
          />
        </Box>
      </Container>
    </div>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    userReducer: state.userReducer,
  }
}

const mapDispatchToProps = {
  obtainApimTokenAndChangeOrganization: obtainApimTokenAndChangeOrganization
}

export default connect(mapStateToProps, mapDispatchToProps)(Forbidden);
